/* eslint-disable react-hooks/exhaustive-deps */
import { Search, Pencil, RotateCw, Copy } from 'lucide-react'
import moment from 'moment'
import { useEffect, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Table,
  Badge
} from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import Select from 'react-select'
import AsyncSelect from 'react-select/async'
import Loader from '../../../components/atoms/Loader'
import TableAction from '../../../components/atoms/Table/TableAction'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import InputDatePicker from '../../../components/Input/DatePicker/InputDatePicker'
import api from '../../../config/AxiosInstance'
import {
  convertDateWithoutTime,
  DELIVERY_TYPES
} from '../../../helpers/constants'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common'
import toast, { Toaster } from 'react-hot-toast'
import { firstLetterUpperCase } from '../../../utils/TextFormatter'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const SelectStyles = {
  control: (base, state) => ({
    ...base,
    height: 45,
    minHeight: 45,
    borderRadius: '10px',
    borderColor: state.isFocused ? 'var(--primary-orange)' : '#e9ecef',
    boxShadow: state.isFocused ? '0 0 0 4px rgba(248, 157, 29, 0.1)' : 'none',
    backgroundColor: '#ffffff',
    border: '1px solid #e9ecef',
    fontSize: '0.9rem'
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    height: '45px',
    padding: '0 12px'
  }),
  input: (provided, state) => ({
    ...provided,
    margin: '0px'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#495057'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'var(--primary-orange)'
      : state.isFocused
        ? '#fff5e6'
        : null,
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer'
  })
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

function Index() {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [prDraftList, setPrDraftList] = useState([])
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }
  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0,
    total_difference_dc: 0
  })

  const [driver, setDriver] = useState('')
  const [debouncedDriver, setDebouncedDriver] = useState('')
  const [selectedWarehouse, setSelectedWarehouse] = useState('')
  const [selectedCustomers, setSelectedCustomers] = useState('')
  const [selectedSjDraft, setSelectedSjDraft] = useState('')
  const [selectedSo, setSelectedSo] = useState('')
  const [deliveryType, setDeliveryType] = useState('')
  const [selectedErpStatus, setSelectedErpStatus] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [warehouses, setWarehouses] = useState([])

  const { per_page, current_page } = meta

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedDriver(driver)
      if (driver !== debouncedDriver) {
        setMeta({ ...meta, current_page: 1 })
      }
    }, 1000)

    return () => {
      clearTimeout(handler)
    }
  }, [driver])

  useEffect(() => {
    if ((startDate && !endDate) || (!startDate && endDate)) {
      return
    }

    fetchDcList()
  }, [
    per_page,
    current_page,
    selectedWarehouse,
    selectedCustomers,
    selectedSjDraft,
    selectedSo,
    deliveryType,
    debouncedDriver,
    startDate,
    endDate,
    selectedErpStatus
  ])

  const fetchWarehouse = async () => {
    try {
      const params = {
        is_parent: 1,
        forUser: 1
      }
      const response = await api.get('/master/warehouses/all', { params })
      const { data } = response.data
      setWarehouses(data)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    }
  }

  useEffect(() => {
    fetchWarehouse()
  }, [])

  const loadCustomerOptions = (inputValue) => {
    return api
      .get('/erp/sales-order/all-customer', { params: { q: inputValue } })
      .then((res) => {
        if (res.data.success) {
          return res.data.data.map((data) => ({
            value: data.customer,
            label: data.customer
          }))
        }
        return []
      })
      .catch(() => [])
  }

  const loadSoOptions = (inputValue) => {
    return api
      .get('/erp/sales-order/all', { params: { q: inputValue } })
      .then((res) => {
        if (res.data.success) {
          return res.data.data.map((data) => ({
            value: data.so,
            label: data.so
          }))
        }
        return []
      })
      .catch(() => [])
  }

  const loadSjOptions = (inputValue) => {
    return api
      .get('/outbound/sjdraft/all-draft', { params: { q: inputValue } })
      .then((res) => {
        if (res.data.success) {
          return res.data.data.map((data) => ({
            value: data.draft_code,
            label: data.draft_code
          }))
        }
        return []
      })
      .catch(() => [])
  }

  const fetchDcList = async () => {
    try {
      setIsLoading(true)
      const params = {
        per_page: meta.per_page,
        page: meta.current_page,
        warehouse_id: selectedWarehouse,
        customer: selectedCustomers,
        erp_sj_no: selectedSjDraft,
        so: selectedSo,
        start_date: startDate,
        end_date: endDate,
        delivery_type: deliveryType,
        driver: debouncedDriver,
        erp_status: selectedErpStatus
      }
      const res = await api.get('outbound/dcdrafts/list', { params })
      const { metadata, data } = res.data
      setPrDraftList(data)
      setMeta((prev) => ({
        ...prev,
        total_row: metadata.total_row,
        total_page: metadata.total_page,
        total_difference_dc: metadata.total_difference_dc || 0
      }))
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setIsLoading(false)
    }
  }

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      current_page: data.selected + 1
    }))
    window.scrollTo(0, 0)
  }

  const handleReset = () => {
    setDriver('')
    setDebouncedDriver('')
    setSelectedWarehouse('')
    setSelectedCustomers('')
    setSelectedSjDraft('')
    setSelectedSo('')
    setStartDate('')
    setEndDate('')
    setDeliveryType('')
    setSelectedErpStatus('')
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  const navigateToDetail = (dc) => {
    navigate(`/outbound/dc/${encodeURIComponent(dc.dc_id)}`)
  }

  return (
    <>
      <Container fluid className="px-4">
        <Row className="mb-4 align-items-center">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <h3 style={headerTitleStyle}>Delivery Confirmation</h3>
              <Button
                variant="light"
                className="text-secondary fw-bold rounded px-3 border"
                onClick={handleReset}
                style={{ fontSize: '0.85rem' }}
              >
                <RotateCw size={16} className="me-2" />
                Reset Filters
              </Button>
            </div>
          </Col>
        </Row>

        <Card style={cardFilterStyle}>
          <Row className="g-3">
            {/* Row 1 */}
            <Col md={3}>
              <Form.Group>
                <Form.Label style={labelStyle}>SOURCE</Form.Label>
                <Select
                  isClearable
                  options={warehouses.map((w) => ({
                    value: w.id,
                    label: w.warehouse_name
                  }))}
                  placeholder="Select Warehouse"
                  styles={SelectStyles}
                  onChange={(e) => {
                    setSelectedWarehouse(e ? e.value : '')
                    setMeta((prev) => ({ ...prev, current_page: 1 }))
                  }}
                  value={
                    selectedWarehouse
                      ? {
                          value: selectedWarehouse,
                          label: warehouses.find(
                            (w) => w.id === selectedWarehouse
                          )?.warehouse_name
                        }
                      : null
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label style={labelStyle}>CUSTOMER</Form.Label>
                <AsyncSelect
                  value={
                    selectedCustomers
                      ? { value: selectedCustomers, label: selectedCustomers }
                      : null
                  }
                  isClearable
                  defaultOptions
                  loadOptions={loadCustomerOptions}
                  placeholder="Select Customer"
                  styles={SelectStyles}
                  onChange={(e) => {
                    setSelectedCustomers(e ? e.value : '')
                    setMeta((prev) => ({ ...prev, current_page: 1 }))
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label style={labelStyle}>#ERP SJ NO.</Form.Label>
                <AsyncSelect
                  value={
                    selectedSjDraft
                      ? { value: selectedSjDraft, label: selectedSjDraft }
                      : null
                  }
                  isClearable
                  defaultOptions
                  loadOptions={loadSjOptions}
                  placeholder="Select SJ Draft"
                  styles={SelectStyles}
                  onChange={(e) => {
                    setSelectedSjDraft(e ? e.value : '')
                    setMeta((prev) => ({ ...prev, current_page: 1 }))
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label style={labelStyle}>#SO</Form.Label>
                <AsyncSelect
                  value={
                    selectedSo ? { value: selectedSo, label: selectedSo } : null
                  }
                  isClearable
                  defaultOptions
                  loadOptions={loadSoOptions}
                  placeholder="Select SO"
                  styles={SelectStyles}
                  onChange={(e) => {
                    setSelectedSo(e ? e.value : '')
                    setMeta((prev) => ({ ...prev, current_page: 1 }))
                  }}
                />
              </Form.Group>
            </Col>

            {/* Row 2 */}
            <Col md={3}>
              <Form.Label style={labelStyle}>START DATE</Form.Label>
              <InputDatePicker
                value={startDate}
                onChange={(date) => {
                  const newDate = date ? moment(date).format('YYYY-MM-DD') : ''
                  setStartDate(newDate)
                  if (newDate && endDate && moment(newDate) > moment(endDate)) {
                    setEndDate(newDate)
                  }
                  setMeta((prev) => ({ ...prev, current_page: 1 }))
                }}
                maxDate={endDate ? new Date(endDate) : null}
              />
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label style={labelStyle}>END DATE</Form.Label>
                <InputDatePicker
                  value={endDate}
                  onChange={(date) => {
                    const newDate = date
                      ? moment(date).format('YYYY-MM-DD')
                      : ''
                    setEndDate(newDate)
                    if (
                      startDate &&
                      newDate &&
                      moment(newDate) < moment(startDate)
                    ) {
                      setStartDate(newDate)
                    }
                    setMeta((prev) => ({ ...prev, current_page: 1 }))
                  }}
                  minDate={startDate ? new Date(startDate) : null}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label style={labelStyle}>DELIVERY TYPE</Form.Label>
                <Select
                  value={
                    deliveryType
                      ? DELIVERY_TYPES.find((d) => d.value === deliveryType)
                      : null
                  }
                  options={DELIVERY_TYPES}
                  onChange={(e) => {
                    setDeliveryType(e ? e.value : '')
                    setMeta((prev) => ({ ...prev, current_page: 1 }))
                  }}
                  placeholder="Select Delivery Type"
                  isClearable
                  styles={SelectStyles}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Label style={labelStyle}>DRIVER</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRight: 'none',
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px'
                  }}
                >
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  name="driver"
                  id="driver"
                  placeholder="Search Driver..."
                  onChange={(e) => setDriver(e.target.value)}
                  value={driver}
                  style={{
                    ...FormControlStyle,
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                />
              </InputGroup>
            </Col>

            <Col md={3}>
              <Form.Group>
                <Form.Label style={labelStyle}>ERP STATUS</Form.Label>
                <Select
                  value={
                    selectedErpStatus
                      ? {
                          value: selectedErpStatus,
                          label:
                            selectedErpStatus === 'submitted'
                              ? 'Submitted'
                              : 'Draft'
                        }
                      : null
                  }
                  options={[
                    { value: 'submitted', label: 'Submitted' },
                    { value: 'draft', label: 'Draft' }
                  ]}
                  onChange={(e) => {
                    setSelectedErpStatus(e ? e.value : '')
                    setMeta((prev) => ({ ...prev, current_page: 1 }))
                  }}
                  placeholder="Select ERP Status"
                  isClearable
                  styles={SelectStyles}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card>

        <Row className="mt-4">
          <Col md={12}>
            <div className="d-flex align-items-center mb-3">
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  backgroundColor: '#FF8F8F',
                  marginRight: '8px'
                }}
              ></div>
              <span className="fw-bold" style={{ color: '#333' }}>
                Total DC with difference :{' '}
                <span style={{ color: '#dc3545' }}>
                  {meta.total_difference_dc || 0}
                </span>
              </span>
            </div>
            <Table
              hover
              responsive
              className="shadow-sm mb-5 bg-white"
              style={{
                borderRadius: '12px',
                overflow: 'hidden',
                fontSize: '0.9rem'
              }}
            >
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Act
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    WMS DC No.
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    ERP SJ No.
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    ERP DC No.
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    #SO
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Driver
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Customer
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Source
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Delivery Type
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Delivery Date
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={10} className="p-5 text-center">
                      <Loader />
                    </td>
                  </tr>
                )}
                {!isLoading && prDraftList.length === 0 && (
                  <tr>
                    <td
                      colSpan={10}
                      className="p-5 text-center fw-bold text-muted"
                    >
                      No data found
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  prDraftList?.map((data, i) => {
                    const rowBgStyle = data?.is_difference
                      ? { backgroundColor: '#FF8F8F' }
                      : {}
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td className="p-2 align-middle" style={rowBgStyle}>
                          <TableAction direction="start">
                            <Button
                              variant="light"
                              className="btn-sm border rounded text-secondary"
                              style={{
                                width: '32px',
                                height: '32px',
                                padding: 0
                              }}
                              onClick={() => navigateToDetail(data)}
                            >
                              {data?.erp_dc_no ? (
                                <Search size={14} />
                              ) : (
                                <Pencil size={14} />
                              )}
                            </Button>
                          </TableAction>
                        </td>
                        <td className="p-2 align-middle" style={rowBgStyle}>
                          <div className="d-flex align-items-center gap-2">
                            {data?.wms_dc_no || '-'}
                            {data?.wms_dc_no && (
                              <Copy
                                size={14}
                                className="text-muted cursor-pointer"
                                style={{
                                  cursor: 'pointer',
                                  opacity: 0.5,
                                  transition: 'opacity 0.2s'
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.opacity = 1)
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.opacity = 0.5)
                                }
                                onClick={() => handleCopy(data?.wms_dc_no)}
                                title="Copy WMS DC Number"
                              />
                            )}
                          </div>
                        </td>
                        <td className="p-2 align-middle" style={rowBgStyle}>
                          <div className="d-flex align-items-center gap-2">
                            {data?.erp_sj_no || '-'}
                            {data?.erp_sj_no && (
                              <Copy
                                size={14}
                                className="text-muted cursor-pointer"
                                style={{
                                  cursor: 'pointer',
                                  opacity: 0.5,
                                  transition: 'opacity 0.2s'
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.opacity = 1)
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.opacity = 0.5)
                                }
                                onClick={() => handleCopy(data?.erp_sj_no)}
                                title="Copy ERP SJ Number"
                              />
                            )}
                          </div>
                        </td>
                        <td className="p-2 align-middle" style={rowBgStyle}>
                          <div className="d-flex align-items-center gap-2">
                            {data?.erp_dc_no || '-'}
                            {data?.erp_dc_no && (
                              <Copy
                                size={14}
                                className="text-muted cursor-pointer"
                                style={{
                                  cursor: 'pointer',
                                  opacity: 0.5,
                                  transition: 'opacity 0.2s'
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.opacity = 1)
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.opacity = 0.5)
                                }
                                onClick={() => handleCopy(data?.erp_dc_no)}
                                title="Copy ERP DC Number"
                              />
                            )}
                          </div>
                        </td>
                        <td className="p-2 align-middle" style={rowBgStyle}>
                          <div className="d-flex align-items-center gap-2">
                            {data.so || '-'}
                            {data.so && (
                              <Copy
                                size={14}
                                className="text-muted cursor-pointer"
                                style={{
                                  cursor: 'pointer',
                                  opacity: 0.5,
                                  transition: 'opacity 0.2s'
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.opacity = 1)
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.opacity = 0.5)
                                }
                                onClick={() => handleCopy(data.so)}
                                title="Copy SO Number"
                              />
                            )}
                          </div>
                        </td>
                        <td className="p-2 align-middle" style={rowBgStyle}>
                          {data.driver}
                        </td>
                        <td className="p-2 align-middle" style={rowBgStyle}>
                          {data.customer}
                        </td>
                        <td className="p-2 align-middle" style={rowBgStyle}>
                          {data.source}
                        </td>
                        <td className="p-2 align-middle" style={rowBgStyle}>
                          <Badge
                            bg={
                              data.delivery_type?.toLowerCase() === 'internal'
                                ? 'primary'
                                : 'warning'
                            }
                            text={
                              data.delivery_type?.toLowerCase() === 'sewa'
                                ? 'dark'
                                : 'light'
                            }
                            className="px-2 py-1"
                          >
                            {firstLetterUpperCase(data.delivery_type || '-')}
                          </Badge>
                        </td>
                        <td className="p-2 align-middle" style={rowBgStyle}>
                          {data.delivery_date
                            ? convertDateWithoutTime(data.delivery_date)
                            : '-'}
                        </td>
                      </tr>
                    )
                  })}
              </tbody>
              <tfoot>
                <tr>
                  <td className="p-3 border-top-0" colSpan={10}>
                    <TablePagination
                      meta={meta}
                      handlePageChange={handlePageChange}
                      handleOnChangeMeta={handleOnChangeMeta}
                    />
                  </td>
                </tr>
              </tfoot>
            </Table>
          </Col>
        </Row>
      </Container>
      <Toaster />
    </>
  )
}

export default Index
